// 📄 pages/SAP-BTP/components/AdaptarValores.tsx
// "Adaptar valores" de un iFlow (docs/plans/plan-visor-diagrama-iflows.md,
// E3): solo el dueño (personal, autor u owner). Muestra los valores REALES
// de la revisión actual (adaptadores, constantes de Content Modifier y
// parameters.prop) para cambiarlos y descargar el .zip adaptado o guardarlo
// como versión nueva del mismo iFlow. El backend reescribe solo esos textos.
// Al descargar también se puede cambiar el nombre y el ID (para importarlo en
// el mismo tenant); al guardar no, porque con otro ID sería otro iFlow.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BookmarkPlus, Download, KeyRound, RefreshCw, RotateCcw, Save, ShieldAlert, Wand2 } from 'lucide-react';
import {
  ID_IFLOW_VALIDO, Iflow, ValorEditable, descargarAdaptado, guardarAdaptadoComoVersion, obtenerValores
} from '../../../services/iflowService.ts';
import { inputCls, labelCls } from './FormDatosIflow.tsx';
import { NOMBRES_CAMPO } from './DiagramaIflow.tsx';
import SelectorPerfil, { tipoParaCampo } from './SelectorPerfil.tsx';
import AplicarPerfilModal from './AplicarPerfilModal.tsx';
import GuardarEnPerfilModal from './GuardarEnPerfilModal.tsx';
import { TipoValorPerfil } from '../../../services/perfilValoresService.ts';

interface AdaptarValoresProps {
  iflow: Iflow;
  // Prefijo de clave a enfocar al abrir (desde "Editar" en el panel del diagrama).
  foco?: string;
  // Guardar como versión (no en una org archivada); descargar siempre.
  puedeGuardar: boolean;
  onGuardado: (version: number, cambiados: number) => Promise<void> | void;
}

const SECCIONES: { tipo: ValorEditable['tipo']; titulo: string; ayuda: string }[] = [
  { tipo: 'adaptador', titulo: 'Adaptadores', ayuda: 'Direcciones, alias de credencial y Cloud Connector de cada sistema.' },
  { tipo: 'contentModifier', titulo: 'Content Modifier (constantes)', ayuda: 'Headers, propiedades y body con valor fijo.' },
  { tipo: 'parametro', titulo: 'Parámetros externalizados', ayuda: 'Valores de parameters.prop.' }
];

/** Tipo de valor de perfil para un cambio (P3): por el campo del adaptador, o URL si lo parece. */
const tipoDeValor = (v: ValorEditable, nuevo: string): TipoValorPerfil =>
  (v.tipo === 'adaptador' && tipoParaCampo(v.campo)) || (nuevo.includes('://') ? 'url' : 'otro');

const etiquetaDe = (v: ValorEditable) =>
  v.tipo === 'adaptador' ? NOMBRES_CAMPO[v.campo] || v.campo
    : v.tipo === 'contentModifier' ? (v.campo === 'Body' ? 'Body' : `${v.campo}: ${v.nombre}`)
    : v.nombre;

export default function AdaptarValores({ iflow, foco = '', puedeGuardar, onGuardado }: AdaptarValoresProps) {
  const [valores, setValores] = useState<ValorEditable[] | null>(null);
  const [version, setVersion] = useState(iflow.versionActual);
  const [editados, setEditados] = useState<Record<string, string>>({});
  const [nota, setNota] = useState('');
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [idNuevo, setIdNuevo] = useState('');
  const contenedorRef = useRef<HTMLDivElement>(null);
  // Campo para el que se abrió "Desde perfil" (P2 de plan-perfiles-valores.md).
  const [desdePerfil, setDesdePerfil] = useState<ValorEditable | null>(null);
  // P3: aplicar un perfil completo / guardar los cambios en un perfil.
  const [aplicandoPerfil, setAplicandoPerfil] = useState(false);
  const [guardandoEnPerfil, setGuardandoEnPerfil] = useState(false);
  const [avisoPerfil, setAvisoPerfil] = useState<string | null>(null);
  // Qué acción está en curso: solo gira ese botón (los dos quedan deshabilitados).
  const [ocupado, setOcupado] = useState<null | 'descargar' | 'guardar'>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    setValores(null);
    setEditados({});
    obtenerValores(iflow.orgId, iflow.id)
      .then((r) => { if (vigente) { setValores(r.valores); setVersion(r.version); } })
      .catch((err) => { if (vigente) setError(err?.message || 'No se pudieron cargar los valores.'); });
    return () => { vigente = false; };
  }, [iflow.orgId, iflow.id, iflow.versionActual]);

  // Solo los que de verdad cambiaron respecto del original.
  const cambios = useMemo(() => {
    const salida: Record<string, string> = {};
    for (const v of valores || []) {
      if (v.clave in editados && editados[v.clave] !== v.valor) salida[v.clave] = editados[v.clave];
    }
    return salida;
  }, [valores, editados]);
  const nCambios = Object.keys(cambios).length;

  // Renombrar: vacío o igual al actual = sin cambio.
  const renombre = {
    name: nombreNuevo.trim() && nombreNuevo.trim() !== iflow.nombreTecnico ? nombreNuevo.trim() : undefined,
    id: idNuevo.trim() && idNuevo.trim() !== iflow.idTecnico ? idNuevo.trim() : undefined
  };
  const hayRenombre = !!(renombre.name || renombre.id);
  const idInvalido = !!renombre.id && !ID_IFLOW_VALIDO.test(renombre.id);

  // Grupo enfocado (desde "Editar" en el diagrama): se resalta y se lleva a la vista.
  const grupoFoco = foco ? valores?.find((v) => v.clave.startsWith(foco))?.grupo || '' : '';
  useEffect(() => {
    if (!grupoFoco) return;
    const campo = contenedorRef.current?.querySelector<HTMLElement>(`[data-clave^="${CSS.escape(foco)}"]`);
    campo?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    campo?.focus({ preventScroll: true });
  }, [grupoFoco, foco]);

  const accion = async (cual: 'descargar' | 'guardar', fn: () => Promise<void>) => {
    setError(null);
    setOcupado(cual);
    try {
      await fn();
    } catch (err: any) {
      setError(err?.message || 'No se pudo completar la acción.');
    } finally {
      setOcupado(null);
    }
  };

  const descargar = () => accion('descargar', () => descargarAdaptado(iflow.orgId, iflow.id, version, cambios, hayRenombre ? renombre : undefined));
  const guardar = () => accion('guardar', async () => {
    const r = await guardarAdaptadoComoVersion(iflow.orgId, iflow.id, version, cambios, nota.trim());
    await onGuardado(r.version, r.cambiados);
  });

  if (error && !valores) return <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>;
  if (!valores) return <div className="p-6 text-center text-gray-400"><RefreshCw className="w-5 h-5 animate-spin mx-auto" /></div>;

  return (
    <div ref={contenedorRef} className="space-y-4">
      <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/30 text-amber-200 p-3 rounded-lg text-xs">
        <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Estás viendo los <strong>valores reales</strong> de la revisión {version}. Los cambios se aplican solo al .zip nuevo: se reemplaza el texto de
          cada valor y el resto del iFlow queda igual. Antes de usarlo en producción, impórtalo en el tenant y revísalo.
        </span>
      </div>

      {foco && !grupoFoco && (
        <p className="text-xs text-gray-400">Ese elemento no tiene valores fijos para adaptar (están parametrizados o se arman en ejecución).</p>
      )}

      <section className="space-y-2">
        <div>
          <h4 className="text-[11px] uppercase tracking-wider text-gray-500">Nombre e ID del iFlow (opcional, solo al descargar)</h4>
          <p className="text-[11px] text-gray-500">Para importarlo en el mismo tenant sin chocar con el original. Con otro ID es otro iFlow, por eso no aplica a "Guardar como versión".</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-white/5 border border-white/10 rounded-xl p-3">
          <div>
            <label className={labelCls}>Nombre</label>
            <input className={`${inputCls} text-xs ${renombre.name ? 'border-cyan-500/60' : ''}`} maxLength={100} value={nombreNuevo}
              onChange={(e) => setNombreNuevo(e.target.value)} placeholder={iflow.nombreTecnico || 'Igual al actual'} />
          </div>
          <div>
            <label className={labelCls}>ID</label>
            <input className={`${inputCls} font-mono text-xs ${idInvalido ? 'border-red-500/60' : renombre.id ? 'border-cyan-500/60' : ''}`} maxLength={100} value={idNuevo}
              onChange={(e) => setIdNuevo(e.target.value)} placeholder={iflow.idTecnico} spellCheck={false} />
            {idInvalido && <p className="text-[11px] text-red-400 mt-1">Solo letras, números, "_", "." y "-".</p>}
          </div>
        </div>
      </section>

      {valores.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 bg-white/5 border border-white/10 rounded-xl p-3">
          <p className="text-[11px] text-gray-400">¿Ya tienes los datos de este cliente en un perfil? Aplícalo y revisa los cambios antes de descargar o guardar.</p>
          <button onClick={() => setAplicandoPerfil(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cyan-500/40 text-xs text-cyan-200 hover:bg-cyan-500/10">
            <Wand2 className="w-3.5 h-3.5" /> Aplicar perfil…
          </button>
        </div>
      )}
      {avisoPerfil && <p className="text-xs text-emerald-300">{avisoPerfil}</p>}

      {valores.length === 0 && <p className="text-sm text-gray-400">Este iFlow no tiene valores para adaptar (todo está parametrizado o se arma en ejecución).</p>}

      {SECCIONES.map(({ tipo, titulo, ayuda }) => {
        const deTipo = valores.filter((v) => v.tipo === tipo);
        if (!deTipo.length) return null;
        const grupos = Array.from(new Set(deTipo.map((v) => v.grupo)));
        return (
          <section key={tipo} className="space-y-3">
            <div>
              <h4 className="text-[11px] uppercase tracking-wider text-gray-500">{titulo}</h4>
              <p className="text-[11px] text-gray-500">{ayuda}</p>
            </div>
            {grupos.map((grupo) => (
              <div key={grupo} className={`bg-white/5 border rounded-xl p-3 space-y-3 ${grupo === grupoFoco ? 'border-cyan-500/60 ring-2 ring-cyan-500/20' : 'border-white/10'}`}>
                {/* Los parámetros son un solo grupo: el título de la sección ya lo dice. */}
                {tipo !== 'parametro' && <p className="text-sm text-white font-medium break-words">{grupo}</p>}
                {deTipo.filter((v) => v.grupo === grupo).map((v) => {
                  const actual = v.clave in editados ? editados[v.clave] : v.valor;
                  const cambiado = actual !== v.valor;
                  const largo = v.campo === 'Body' || v.valor.length > 90 || actual.includes('\n');
                  const props = {
                    value: actual,
                    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setEditados((ed) => ({ ...ed, [v.clave]: e.target.value })),
                    className: `${inputCls} font-mono text-xs ${cambiado ? 'border-cyan-500/60' : ''}`,
                    maxLength: 4000,
                    spellCheck: false,
                    'data-clave': v.clave,
                    placeholder: '(vacío)'
                  };
                  return (
                    <div key={v.clave}>
                      <div className="flex items-center justify-between gap-2">
                        <label className={labelCls}>
                          {etiquetaDe(v)}
                          {v.dinamico && <span className="text-gray-500 font-normal"> · se arma en ejecución (${'{'}…{'}'})</span>}
                        </label>
                        <div className="flex items-center gap-3 mb-1">
                          <button onClick={() => setDesdePerfil(v)} className="inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-cyan-300"
                            title="Insertar un valor guardado en un perfil de valores">
                            <KeyRound className="w-3 h-3" /> Desde perfil
                          </button>
                          {cambiado && (
                            <button onClick={() => setEditados((ed) => { const { [v.clave]: _, ...resto } = ed; return resto; })}
                              className="inline-flex items-center gap-1 text-[11px] text-cyan-300 hover:text-cyan-200">
                              <RotateCcw className="w-3 h-3" /> Restaurar
                            </button>
                          )}
                        </div>
                      </div>
                      {largo ? <textarea rows={3} {...props} /> : <input {...props} />}
                    </div>
                  );
                })}
              </div>
            ))}
          </section>
        );
      })}

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

      {aplicandoPerfil && (
        <AplicarPerfilModal
          orgId={iflow.orgId}
          valores={valores.map((v) => ({ clave: v.clave, valor: v.clave in editados ? editados[v.clave] : v.valor, etiqueta: `${v.grupo} · ${etiquetaDe(v)}` }))}
          onAplicar={(c) => {
            setEditados((ed) => ({ ...ed, ...c }));
            setAplicandoPerfil(false);
            setAvisoPerfil(`Se aplicaron ${Object.keys(c).length} cambio${Object.keys(c).length === 1 ? '' : 's'} del perfil. Revísalos (resaltados) antes de descargar o guardar.`);
          }}
          onCerrar={() => setAplicandoPerfil(false)}
        />
      )}
      {guardandoEnPerfil && (
        <GuardarEnPerfilModal
          orgId={iflow.orgId}
          cambios={(valores || []).filter((v) => v.clave in cambios).map((v) => ({
            etiqueta: `${v.grupo} · ${etiquetaDe(v)}`,
            tipo: tipoDeValor(v, cambios[v.clave]),
            valor: cambios[v.clave],
            reemplaza: v.valor
          }))}
          onListo={(m) => { setGuardandoEnPerfil(false); setAvisoPerfil(m); }}
          onCerrar={() => setGuardandoEnPerfil(false)}
        />
      )}
      {desdePerfil && (
        <SelectorPerfil
          orgId={iflow.orgId}
          tipoSugerido={desdePerfil.tipo === 'adaptador' ? tipoParaCampo(desdePerfil.campo) : null}
          onElegir={(valor) => { setEditados((ed) => ({ ...ed, [desdePerfil.clave]: valor })); setDesdePerfil(null); }}
          onCerrar={() => setDesdePerfil(null)}
        />
      )}

      <div className="border-t border-white/10 pt-4 space-y-3">
        <p className="text-xs text-gray-400">
          {nCambios === 0 ? 'Todavía no cambiaste ningún valor.' : `${nCambios} valor${nCambios === 1 ? '' : 'es'} cambiado${nCambios === 1 ? '' : 's'}.`}
          {hayRenombre && ' Con nombre/ID nuevo: solo se puede descargar.'}
        </p>
        {puedeGuardar && (
          <div>
            <label className={labelCls}>Nota de la versión (opcional, si la guardas)</label>
            <input className={inputCls} maxLength={500} value={nota} onChange={(e) => setNota(e.target.value)} placeholder={`Ej. Datos de Cliente B (por defecto: "Valores adaptados")`} />
          </div>
        )}
        {puedeGuardar && iflow.estado !== 'borrador' && (
          <p className="text-[11px] text-amber-300">Al guardar como versión nueva, el iFlow vuelve a Borrador (la aprobación era de la revisión {iflow.aprobacion?.revision ?? iflow.versionActual}).</p>
        )}
        <div className="flex flex-wrap gap-2 justify-end">
          <button onClick={() => setGuardandoEnPerfil(true)} disabled={!!ocupado || nCambios === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-white/10 text-sm text-gray-200 hover:border-cyan-500/40 hover:text-white disabled:opacity-40 mr-auto"
            title="Guarda estos cambios en un perfil de valores, para aplicarlos de golpe la próxima vez">
            <BookmarkPlus className="w-4 h-4" /> Guardar en un perfil
          </button>
          <button onClick={descargar} disabled={!!ocupado || (nCambios === 0 && !hayRenombre) || idInvalido}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-white/10 text-sm text-gray-200 hover:border-cyan-500/40 hover:text-white disabled:opacity-40">
            {ocupado === 'descargar' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Descargar .zip adaptado
          </button>
          {puedeGuardar && (
            <button onClick={guardar} disabled={!!ocupado || nCambios === 0 || hayRenombre}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-40">
              {ocupado === 'guardar' ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar como versión {iflow.versionActual + 1}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
